import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, Output, ViewChild } from '@angular/core';

import * as am4core from '@amcharts/amcharts4/core';

import { ChartExportEventModel } from '../../models/chart-export-event.model';
import { ActionKeys } from '../../../Forms/enums/keys.enum';
import { DomHandler } from 'primeng/dom';

export enum MenuType {
    MAIN,
    IMAGE,
    DATA
}

@Component({
    selector: 'agpr-chart-export-menu',
    templateUrl: './export-menu.component.html',
    styleUrls: ['./export-menu.component.scss'],
    standalone: false
})
export class ExportMenuComponent implements AfterViewInit {
    @Input() id: string = null;
    @Input() chartIdentifier: string;
    @Input() iconStyleClass: string;
    @Input() set supportedFormats(formats: string[]) {
        if (!formats) {
            this.formatsMap = {
                png: true,
                gif: true,
                jpg: true,
                svg: true,
                // pdf: true,
                csv: true,
                json: true,
                // xlsx: true,
                print: true,
            };
            this.showImagesSection = true;
            this.showDataSection = true;
            return;
        }

        this.formatsMap = {};
        formats.forEach(format => {
            this.formatsMap[format] = true;
        });

        this.showImagesSection = !!this.imageFormats.find((format) => this.formatsMap[format]);
        this.showDataSection = !!this.dataFormats.find((format) => this.formatsMap[format]);
    }
    @Input() contextMenu: boolean = true;
    @Input() social: boolean = false;

    @Output() exportMenuClicked: EventEmitter<ChartExportEventModel>;

    @ViewChild('menu') menu: ElementRef;
    @ViewChild('button') button: ElementRef;

    menuElement: HTMLDivElement;
    menuWidth: string = '5.2rem';
    buttonElement: HTMLButtonElement;

    formatsMap: any = {
        png: true,
        gif: true,
        jpg: true,
        svg: true,
        // pdf: true,
        csv: true,
        json: true,
        // xlsx: true,
        print: true,
    };
    // @TODO: PDF and XLSX are broken when built because it tries to load the pdfMake chunk with a base path of /
    // https://github.com/amcharts/export#2-manual has more details, but the files won't have the expected
    // names because we're adding a hash to the files
    imageFormats: (keyof am4core.IExportOptions)[] = [
        'png',
        'gif',
        'jpg',
        'svg',
        // 'pdf'
    ];
    dataFormats: (keyof am4core.IExportOptions)[] = [
        'csv',
        'json',
        // 'xlsx'
    ];
    showMainMenu: boolean = false;
    showImagesSection: boolean = true;
    showImagesMenu: boolean = false;
    showDataSection: boolean = true;
    showDataMenu: boolean = false;

    readonly menuEnum = MenuType;
    readonly actionKeysEnum = ActionKeys;

    constructor(private cd: ChangeDetectorRef) {
        this.exportMenuClicked = new EventEmitter<ChartExportEventModel>();
    }

    ngAfterViewInit(): void {
        this.buttonElement = this.button.nativeElement;
        this.menuElement = this.menu.nativeElement;
        document.body.appendChild(this.menuElement);
    }

    public exportMenuItemClicked(event, format): void {
        const chartExportEvent = new ChartExportEventModel();

        chartExportEvent.chartId = this.chartIdentifier;
        chartExportEvent.eventKey = format;

        this.exportMenuClicked.emit(chartExportEvent);
        event.stopPropagation();
    }

    toggleMainMenu() {
        this.showMainMenu = !this.showMainMenu;
        this.cd.detectChanges();
    }

    showMenu(menu: MenuType) {
        switch (menu) {
            case MenuType.MAIN:
                this.showMainMenu = true;
                this.buttonElement.focus();
                this.alignPanel();
                break;
            case MenuType.IMAGE:
                this.showImagesMenu = true;
                break;
            case MenuType.DATA:
                this.showDataMenu = true;
                break;
        }
        this.cd.detectChanges();
    }

    hideMenu(menu: MenuType, event?) {
        if (!!event && !this.menuElement.contains(event?.relatedTarget)) {
            this.showMainMenu = false;
        }
        switch (menu) {
            case MenuType.IMAGE:
                this.showImagesMenu = false;
                break;
            case MenuType.DATA:
                this.showDataMenu = false;
                break;
        }
        this.cd.detectChanges();
    }

    //#region region keyboard accessibility

    /**
     * Handles navigation between main menu and submenu, and escaping the menu.
     * Pressing Escape with any item focused closes the menu. The focus will automatically return to the menu button.
     * Pressing Left Arrow, Tab or Enter on the main menu moves the focus into the sub menu.
     * Pressing Right Arrow on the sub menu moves the focus back to the main menu (Shift + Tab also works by default).
     * @param event keyboard event
     * @returns void
     */
    onKeydown(event: KeyboardEvent): void {
        if (event.key === this.actionKeysEnum.ESCAPE) {
            this.hideMenu(MenuType.MAIN);
            return;
        }
        if ((event.target as HTMLElement).classList.contains('agpr-hoverMenu-item')) {
            if (event.key === this.actionKeysEnum.TAB || event.key === this.actionKeysEnum.LEFT || event.key === this.actionKeysEnum.ENTER) {
                event.preventDefault();
                const subListItem = (event.target as HTMLElement).querySelector('.agpr-hoverMenu-subitem');
                (subListItem as HTMLElement).focus();
            }
        } else if ((event.target as HTMLElement).classList.contains('agpr-hoverMenu-subitem') && event.key === this.actionKeysEnum.RIGHT) {
            const mainListItem = (event.target as HTMLElement).closest('.agpr-hoverMenu-item');
            (mainListItem as HTMLElement).focus();
        }
    }

    /**
     * Handles the selection of an export format via Enter or Space keys
     * @param event keyboard event
     * @param format selected export format
     */
    onItemKeyDown(event: KeyboardEvent, format): void {
        if (event.key === this.actionKeysEnum.ENTER || event.key === this.actionKeysEnum.SPACE) {
            this.exportMenuItemClicked(event, format);
        }
        if (event.key === this.actionKeysEnum.TAB) {
            this.showMainMenu = false;
            this.showImagesMenu = false;
            this.showDataMenu = false;
            this.buttonElement.focus();
        }
    }

    /**
     * Moves focus into the main menu when the down arrow or tab is pressed.
     * @param event keyboard event
     */
    onButtonKeydown(event: KeyboardEvent) {
        if (this.showMainMenu && (event.key === this.actionKeysEnum.DOWN || (event.key === this.actionKeysEnum.TAB && !event.shiftKey))) {
            event.preventDefault();
            this.menu.nativeElement.querySelector('.agpr-hoverMenu-item').focus();
        }
    }

    /**
     * Closes the menu when the focus leaves the export menu.
     * @param event focus event
     */
    onFocusOut(event): void {
        if (!event.relatedTarget?.classList.contains('agpr-hoverMenu-item') && !event.relatedTarget?.classList.contains('agpr-hoverMenu-subitem')) {
            this.hideMenu(MenuType.MAIN);
        }
    }

    //#endregion keyboard accessibility

    alignPanel() {
        DomHandler.absolutePosition(this.menuElement, this.buttonElement);
        const buttonRect = this.buttonElement.getBoundingClientRect();

        this.menuElement.style.left = 'unset';
        this.menuElement.style.right = window.innerWidth - buttonRect.right + 'px';
    }
}
